import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(() => JSON.parse(localStorage.getItem('user') || 'null'));
  const [token,   setToken]   = useState(() => localStorage.getItem('token') || null);
  const [loading, setLoading] = useState(true);

  // Verify token on mount
  useEffect(() => {
    if (token) {
      authAPI.getMe()
        .then(res => { setUser(res.data); localStorage.setItem('user', JSON.stringify(res.data)); })
        .catch(() => { logout(); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  const login = useCallback(async (email, password) => {
    const res = await authAPI.login({ email, password });
    const { token: t, user: u } = res.data;
    localStorage.setItem('token', t);
    localStorage.setItem('user', JSON.stringify(u));
    setToken(t);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  }, []);

  const isAdmin          = user?.role === 'admin';
  const isSuperAdmin     = user?.role === 'super_admin';
  const isUser           = user?.role === 'user';
  const isManagement     = user?.role === 'management';
  const isClassTeacher   = user?.role === 'class_teacher';
  const isSubjectTeacher = user?.role === 'subject_teacher';
  const isCoordinator    = user?.role === 'coordinator';
  const isVicePrincipal  = user?.role === 'vice_principal';
  const isPrincipal      = user?.role === 'principal';
  const isSchoolWide     = ['admin','super_admin','vice_principal','principal','management'].includes(user?.role);
  const canEdit          = isAdmin || isSuperAdmin || isUser; // admins can always edit

  // hasModule(key) — true if the user can VIEW the module
  const hasModule = useCallback((key) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'super_admin') return true;
    if (!user.permissions || user.permissions === null) return true;
    return user.permissions.some(p => p.module === key && p.can_view);
  }, [user]);

  // canDoAction(key, action) — checks a specific action on a module
  // action: 'view' | 'add' | 'edit' | 'delete' | 'approve' | 'publish' | 'export'
  const canDoAction = useCallback((key, action = 'view') => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'super_admin') return true;
    if (!user.permissions || user.permissions === null) return true;
    const perm = user.permissions.find(p => p.module === key);
    if (!perm || !perm.can_view) return false;
    const colMap = { view: 'can_view', add: 'can_add', edit: 'can_edit', delete: 'can_delete', approve: 'can_approve', publish: 'can_publish', export: 'can_export' };
    return !!(perm[colMap[action] || 'can_view']);
  }, [user]);

  // canEditModule(key) — backward-compatible alias for canDoAction(key, 'edit')
  const canEditModule = useCallback((key) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'super_admin') return true;
    if (!user.permissions || user.permissions === null) return true;
    return user.permissions.some(p => p.module === key && p.can_edit);
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, logout,
      isAdmin, isSuperAdmin, isUser, isManagement, canEdit,
      isClassTeacher, isSubjectTeacher, isCoordinator, isVicePrincipal, isPrincipal, isSchoolWide,
      hasModule, canDoAction, canEditModule,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};
